import "./CartSummary.scss";
import React, { useState, useRef, useEffect } from "react";
import { FormControl, TextField } from "@mui/material";
import Map from "../../components/Map/Map";
import AddIcon from "@mui/icons-material/AddBoxOutlined";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeftOutlined";
import ArrowRightIcon from "@mui/icons-material/ArrowRightOutlined";
import ArrowDownwardIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import Address from "../../assets/images/address-icon.png";
import CoponImg from "../../assets/images/copon-img.svg";
import LocationIcon from "../../assets/images/location-icon.png";
import GreenButton from "../GreenButton/GreenButton";
import { useLanguage } from "../../context/LanguageContext";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useCart } from "../../context/CartContext";
import api from "../../utils/api";

const CartSummary = ({
  onConfirmOrder,
  loading,
  appointmentRequired = true,
  selectedPayment, // Receive from parent
  onPaymentChange, // Receive from parent
}) => {
  const { language, t, dir } = useLanguage();
  const navigate = useNavigate();
  const [openMap, setOpenMap] = useState(false);
  const [addressOpen, setAddressOpen] = useState(false);
  const [discountCode, setDiscountCode] = useState("");
  const [discountMessage, setDiscountMessage] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [messageType, setMessageType] = useState(""); // success or error
  const addressRef = useRef(null);
  const { user } = useAuth();
  console.log("CartSummary user:", user);

  // Use the cart context for all cart-related state
  const {
    hasServices,
    hasUtilityItems,
    hasPackages,
    hasItems,
    selectedAddress,
    updateSelectedAddress,
    appointmentDetails,
    promoCode,
    applyPromoCode,
    removePromoCode,
    calculateTotalPrice,
    updateCurrentUser,
  } = useCart();

  // Get price information from the cart context
  const { servicesPrice, utilityPrice, packagesPrice, totalPrice } = calculateTotalPrice();

  const userAddresses = user?.address || [];
  const hasUserAddresses = userAddresses.length > 0;

  // Check if there's an appointment set (only needed if appointment is required)
  const hasAppointment = Boolean(appointmentDetails);
  // Flag to determine if proceed button should be enabled
  const canProceed = hasItems && (!appointmentRequired || hasAppointment);

  // Update the currentUser in CartContext when user changes
  useEffect(() => {
    if (user) {
      updateCurrentUser(user);
    }
  }, [user, updateCurrentUser]);

  // Reset selectedAddress if user has no addresses
  useEffect(() => {
    if (!hasUserAddresses && selectedAddress) {
      // If user has no addresses but a selected address exists, clear it
      updateSelectedAddress(null);
    }
  }, [hasUserAddresses, selectedAddress, updateSelectedAddress]);

  const handleAddressToggle = () => {
    setAddressOpen(!addressOpen);
  };

  const handleClickAway = (event) => {
    if (addressRef.current && !addressRef.current.contains(event.target)) {
      setAddressOpen(false);
    }
  };

  const handleApplyDiscount = async () => {
    if (!discountCode.trim()) {
      setDiscountMessage(
        language === "ar"
          ? "الرجاء إدخال كود الخصم"
          : "Please enter a discount code"
      );
      setMessageType("error");
      return;
    }

    try {
      setIsVerifying(true);
      setDiscountMessage("");

      // Call API to verify the promo code
      const response = await api.get(`/promoCodes/${discountCode}/verify`);

      if (response.data.status === "success") {
        // Extract discount data from response
        const promoCodeData = {
          code: response.data.data.code,
          discount: response.data.data.discount,
          maxDiscount: response.data.data.maxDiscount,
        };

        // Update discount in cart context
        applyPromoCode(promoCodeData);

        // Show success message
        setDiscountMessage(
          language === "ar"
            ? "تم تطبيق كود الخصم"
            : "Discount code applied successfully"
        );
        setMessageType("success");

        // Clear input field after successful application
        setDiscountCode("");
      } else {
        // This case should not happen based on the API structure, but just in case
        removePromoCode();
        setDiscountMessage(
          response.data.message ||
            (language === "ar" ? "كود الخصم غير صالح" : "Invalid discount code")
        );
        setMessageType("error");
      }
    } catch (error) {
      removePromoCode();

      // Handle error message from API
      if (error.response && error.response.data) {
        setDiscountMessage(
          error.response.data.message ||
            (language === "ar" ? "كود الخصم غير صالح" : "Invalid discount code")
        );
      } else {
        setDiscountMessage(
          language === "ar"
            ? "حدث خطأ أثناء التحقق من كود الخصم"
            : "Error verifying discount code"
        );
      }
      setMessageType("error");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleRemovePromoCode = () => {
    removePromoCode();
    setDiscountMessage("");
  };

  const handleBookAppointment = () => {
    navigate("/services");
  };

  const handleConfirmOrder = () => {
    // Call the parent's onConfirmOrder function
    onConfirmOrder();
  };

  // Format the user's address
  const formatUserAddress = (addressObj) => {
    if (!addressObj) return "";

    if (language === "ar") {
      return `${addressObj.city || ""}، ${addressObj.region || ""}، شارع ${
        addressObj.street || ""
      }، مبنى ${addressObj.buildingNo || ""}، طابق ${addressObj.floorNo || ""}`;
    } else {
      return `${addressObj.city || ""}, ${addressObj.region || ""}, ${
        addressObj.street || ""
      } St., Building ${addressObj.buildingNo || ""}, Floor ${
        addressObj.floorNo || ""
      }`;
    }
  };

  // Initialize selectedAddress from user addresses if it's not set yet
  useEffect(() => {
    if (hasUserAddresses && userAddresses.length > 0) {
      // Only update if there are addresses available
      if (!selectedAddress) {
        // If no address is selected, select the first one
        updateSelectedAddress(userAddresses[0]);
      } else {
        // If an address is selected, make sure it still exists in the user's addresses
        const addressStillExists = userAddresses.some(
          (addr) => addr._id === selectedAddress._id
        );
        if (!addressStillExists) {
          // If the selected address no longer exists, select the first available address
          updateSelectedAddress(userAddresses[0]);
        }
      }
    }
  }, [hasUserAddresses, selectedAddress, userAddresses, updateSelectedAddress]);

  useEffect(() => {
    if (addressOpen) {
      document.addEventListener("mousedown", handleClickAway);
    } else {
      document.removeEventListener("mousedown", handleClickAway);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickAway);
    };
  }, [addressOpen]);

  // Handle address selection - just pass address data
  const handleAddressSelection = (index) => {
    const addressData = userAddresses[index];
    updateSelectedAddress(addressData);
  };

  // Labels based on language
  const getServicesLabel = () => {
    return language === "ar" ? "سعر الخدمات" : "Services Price";
  };

  const getUtilityItemsLabel = () => {
    return language === "ar" ? "سعر المستلزمات" : "Utility Items Price";
  };

  const getPackagesLabel = () => {
    return language === "ar" ? "سعر الاشتراكات" : "Packages Price";
  };

  const getDiscountLabel = () => {
    return language === "ar" ? "خصم" : "Discount";
  };

  return (
    <div className="info">
      <div className="info-container">
        <div className="info-container-address" onClick={handleAddressToggle}>
          <img src={Address} alt="" />
          {selectedAddress ? (
            <p className="selected-address-container">
              <span className="selected-address-name">
                {selectedAddress.name}
              </span>
              {formatUserAddress(selectedAddress)}
            </p>
          ) : (
            <p>
              <span>{t("no_address")}</span>
              {language === "ar"
                ? " قم بالضغط لاضافة عنوان"
                : "Click to add address"}
            </p>
          )}

          <ArrowDownwardIcon className="icon" />

          {addressOpen && (
            <div ref={addressRef} className="address-container">
              {/* Map through user addresses if available */}
              {hasUserAddresses ? (
                userAddresses.map((address, index) => (
                  <div
                    className={`address-item ${
                      selectedAddress && selectedAddress._id === address._id
                        ? "selected"
                        : ""
                    }`}
                    key={address._id || index}
                    onClick={() => handleAddressSelection(index)}
                  >
                    <div className="img-container">
                      <img src={LocationIcon} alt="" />
                    </div>
                    <div className="text-container">
                      <p>{address.name}</p>
                      <span>{formatUserAddress(address)}</span>
                    </div>
                  </div>
                ))
              ) : (
                // No addresses message
                <div className="no-address-message">
                  <p>
                    {language === "ar"
                      ? "لا توجد عناوين محفوظة"
                      : "No saved addresses"}
                  </p>
                </div>
              )}

              <div
                className="address-header"
                onClick={() => {
                  setOpenMap(true);
                }}
              >
                <AddIcon />
                <p className="add-address">{t("add_new_address")}</p>
              </div>
            </div>
          )}
        </div>

        <Map open={openMap} onClose={() => setOpenMap(false)} />

        {/* Show warning if no appointment is set but appointment is required */}
        {hasItems && appointmentRequired && !hasAppointment && (
          <div className="appointment-warning">
            <p>{t("no_appointment_scheduled")}</p>
            <GreenButton
              text={t("book_appointment")}
              variant="fill"
              size="medium"
              onClick={handleBookAppointment}
            />
          </div>
        )}

        <div className="payment-methods">
          <p>{t("available_payment_method")}</p>
          <div className="payment-groups">
            {/* Default Payment Methods Group */}
            <div
              className={`payment-group ${
                selectedPayment === "default" ? "selected" : ""
              }`}
              onClick={() => onPaymentChange("default")}
            >
              <div className="payment-group-header">
                <span>
                  {language === "ar"
                    ? "الطرق التقليدية"
                    : "Traditional Methods"}
                </span>
              </div>
              <div className="methods">
                <div className="payment-icon">
                  <div className="payment-icon-img">
                    <img
                      src="https://framerusercontent.com/images/iF6pDpliThuRXQ0cL1YKrx5xjnk.png?scale-down-to=512"
                      alt="Visa"
                      className="card-img visa"
                    />
                    <img
                      src="https://framerusercontent.com/images/lrnNBBmyhNdZrHUzr30XXjtCaZ0.png?scale-down-to=512"
                      alt="MasterCard"
                      className="card-img mastercard"
                    />
                  </div>
                  <span>
                    {language === "ar" ? "بطاقة إئتمان" : "Credit Card"}
                  </span>
                </div>

                <div className="payment-icon">
                  <div className="payment-icon-img">
                    <img
                      src="https://framerusercontent.com/images/hTqGFwU0NDFFuXk3EFMBCGrggYw.png?scale-down-to=512"
                      alt="STC Pay"
                    />
                  </div>
                  <span>STC Pay</span>
                </div>

                <div className="payment-icon">
                  <div className="payment-icon-img">
                    <img
                      src="https://framerusercontent.com/images/G2amYybXOGkMbmX9jPdwVkvYu6I.png?scale-down-to=512"
                      alt="Apple Pay"
                    />
                  </div>
                  <span>Apple Pay</span>
                </div>
              </div>
            </div>

            {/* Tamara Payment Method Group */}
            <div
              className={`payment-group tamara-group ${
                selectedPayment === "tamara" ? "selected" : ""
              }`}
              onClick={() => onPaymentChange("tamara")}
            >
              <div className="payment-group-header">
                <span>
                  {language === "ar"
                    ? "اشتري الآن وادفع لاحقاً"
                    : "Buy Now, Pay Later"}
                </span>
              </div>
              <div className="methods">
                <div className="payment-icon">
                  <div className="payment-icon-img">
                    <img
                      src={
                        language === "ar"
                          ? "https://m.eyeofriyadh.com/directory/images/2022/12/1481b19eacb84.jpg"
                          : "https://dka575ofm4ao0.cloudfront.net/pages-transactional_logos/retina/254863/Logo_Badge-2.png"
                      }
                      alt="Tamara"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Price Summary Section - Only show if there are items */}
        {hasItems ? (
          <>
            <div className="selected-method">
              {/* Show services price if there are services */}
              {hasServices && (
                <div className="item">
                  <span className="text">{getServicesLabel()}</span>
                  <span className="price">
                    {servicesPrice} {t("currency")}
                  </span>
                </div>
              )}

              {/* Show utility items price if there are utility items */}
              {hasUtilityItems && (
                <div className="item">
                  <span className="text">{getUtilityItemsLabel()}</span>
                  <span className="price">
                    {utilityPrice} {t("currency")}
                  </span>
                </div>
              )}

              {/* Show packages price if there are packages */}
              {hasPackages && (
                <div className="item">
                  <span className="text">{getPackagesLabel()}</span>
                  <span className="price">
                    {packagesPrice} {t("currency")}
                  </span>
                </div>
              )}

              {/* Always show discount section if discount is applied */}
              {promoCode && (
                <div className="item discount-item">
                  <span className="text">
                    {getDiscountLabel()}
                    {promoCode.code && (
                      <span className="promo-code-label">
                        ({promoCode.code})
                      </span>
                    )}
                    <button
                      className="remove-promo"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemovePromoCode();
                      }}
                    >
                      {language === "ar" ? "إزالة" : "Remove"}
                    </button>
                  </span>
                  <span className="price discount-value">
                    -{calculateTotalPrice().discount.toFixed(2)} {t("currency")}
                  </span>
                </div>
              )}
            </div>

            {/* Only show promo code input if there are items and no promo code is applied */}
            {hasItems && !promoCode && (
              <div className="promo-code" dir={dir}>
                <FormControl sx={{ flex: 1, width: "100%" }}>
                  <img src={CoponImg} alt="" />
                  <label className="form-label">{t("promo_code")}</label>
                  <button
                    type="button"
                    className={`active-btn ${isVerifying ? "loading" : ""}`}
                    onClick={handleApplyDiscount}
                    disabled={isVerifying}
                  >
                    {isVerifying
                      ? dir === "rtl"
                        ? "جاري التحقق..."
                        : "Verifying..."
                      : t("apply")}
                  </button>
                  <TextField
                    fullWidth
                    className="form-input"
                    placeholder={t("enter_your_code_here")}
                    value={discountCode}
                    onChange={(e) => setDiscountCode(e.target.value)}
                    error={messageType === "error"}
                  />
                </FormControl>
                {discountMessage && (
                  <p className={`discount-message ${messageType}`}>
                    {discountMessage}
                  </p>
                )}
              </div>
            )}

            <div className="total-container">
              <div className="total">
                <span className="text">{t("total")}</span>
                <span className="price">
                  {totalPrice.toFixed(2)} {t("currency")}
                </span>
              </div>
              <div className="img-container">
                <GreenButton
                  text={t("confirm_payment")}
                  variant="fill"
                  size="large"
                  onClick={handleConfirmOrder}
                  loading={loading}
                  disabled={!canProceed}
                />
              </div>
            </div>
          </>
        ) : (
          // No items message and buttons
          <div className="no-items-container">
            <p className="no-items-message">{t("no_items_added")}</p>
            <div className="shopping-buttons">
              <GreenButton
                text={t("browse_services")}
                variant="fill"
                size="medium"
                onClick={() => navigate("/services")}
              />
              <GreenButton
                text={t("browse_subscriptions")}
                variant="outlined"
                size="medium"
                onClick={() => navigate("/packages")}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartSummary;